import * as THREE from 'three';
import type { PoseProvider } from './PoseProvider';

const EYE_HEIGHT_M = 1.6;

/**
 * デスクトップ検証用ポーズ供給(計画書Phase 3の「デスクトップで検証してから実機へ」)。
 * マウスドラッグで視線回転、WASD/矢印キーで移動。史跡原点に立った状態から開始。
 * `?pose=desktop` で強制、センサー非搭載環境では自動選択される。
 */
export class DesktopPoseProvider implements PoseProvider {
  readonly kind = 'desktop' as const;
  private yawRad = 0;
  private pitchRad = 0;
  private yawOffsetRad = 0;
  private pos = new THREE.Vector3(0, EYE_HEIGHT_M, 0);
  private keys = new Set<string>();
  private dragging = false;
  private lastX = 0;
  private lastY = 0;
  private cleanup: (() => void)[] = [];
  private euler = new THREE.Euler(0, 0, 0, 'YXZ');
  private lastTime = performance.now();

  constructor(private el: HTMLElement) {}

  async start(): Promise<void> {
    const onDown = (e: PointerEvent) => {
      this.dragging = true;
      this.lastX = e.clientX;
      this.lastY = e.clientY;
    };
    const onMove = (e: PointerEvent) => {
      if (!this.dragging) return;
      this.yawRad -= (e.clientX - this.lastX) * 0.004;
      this.pitchRad = THREE.MathUtils.clamp(this.pitchRad - (e.clientY - this.lastY) * 0.004, -1.4, 1.4);
      this.lastX = e.clientX;
      this.lastY = e.clientY;
    };
    const onUp = () => (this.dragging = false);
    const onKeyDown = (e: KeyboardEvent) => this.keys.add(e.key.toLowerCase());
    const onKeyUp = (e: KeyboardEvent) => this.keys.delete(e.key.toLowerCase());

    this.el.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    this.cleanup.push(() => {
      this.el.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    });
  }

  stop(): void {
    for (const c of this.cleanup) c();
    this.cleanup = [];
  }

  setYawOffsetDeg(deg: number): void {
    this.yawOffsetRad = (deg * Math.PI) / 180;
  }

  get ready(): boolean {
    return true;
  }

  update(camera: THREE.PerspectiveCamera, _contentGroup: THREE.Group): void {
    const now = performance.now();
    const dt = Math.min((now - this.lastTime) / 1000, 0.1);
    this.lastTime = now;

    const speed = this.keys.has('shift') ? 30 : 8; // m/s
    const forward = new THREE.Vector3(-Math.sin(this.yawRad), 0, -Math.cos(this.yawRad));
    const right = new THREE.Vector3(-forward.z, 0, forward.x);
    if (this.keys.has('w') || this.keys.has('arrowup')) this.pos.addScaledVector(forward, speed * dt);
    if (this.keys.has('s') || this.keys.has('arrowdown')) this.pos.addScaledVector(forward, -speed * dt);
    if (this.keys.has('a') || this.keys.has('arrowleft')) this.pos.addScaledVector(right, -speed * dt);
    if (this.keys.has('d') || this.keys.has('arrowright')) this.pos.addScaledVector(right, speed * dt);
    if (this.keys.has('q')) this.pos.y += speed * dt;
    if (this.keys.has('e')) this.pos.y = Math.max(EYE_HEIGHT_M, this.pos.y - speed * dt);

    this.euler.set(this.pitchRad, this.yawRad + this.yawOffsetRad, 0);
    camera.quaternion.setFromEuler(this.euler);
    camera.position.copy(this.pos);
  }
}
